/**
 * Modal for picking (or uploading) an image from the user's My Images
 * library — used for QR logos and image-based redirect destinations.
 */
import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2, Upload, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { imagesApi } from '../../api/services';
import type { ImageItem } from '../../types/image.types';
import { Button } from '../Button';

interface ImagePickerModalProps {
  onSelect: (image: ImageItem) => void;
  onClose: () => void;
}

export function ImagePickerModal({ onSelect, onClose }: ImagePickerModalProps) {
  const [images, setImages] = useState<ImageItem[]>([]);
  const [imageCount, setImageCount] = useState(0);
  const [imageLimit, setImageLimit] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    imagesApi
      .listImages({ page_size: 100 })
      .then((res) => {
        setImages(res.images);
        setImageCount(res.image_count);
        setImageLimit(res.image_limit);
      })
      .catch(() => toast.error('Failed to load images'))
      .finally(() => setLoading(false));
  }, []);

  const quotaReached = imageLimit > 0 && imageCount >= imageLimit;

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be 2MB or smaller');
      return;
    }

    setUploading(true);
    try {
      const uploaded = await imagesApi.uploadImage(file);
      setImages((prev) => [uploaded, ...prev]);
      setImageCount((prev) => prev + 1);
      onSelect(uploaded);
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 403) {
        toast.error(detail || 'Image limit reached. Please upgrade your subscription to upload more images.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to upload image');
      }
    } finally {
      setUploading(false);
    }
  };

  const scrollByAmount = (amount: number) => {
    trackRef.current?.scrollBy({ left: amount, behavior: 'smooth' });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-[--clinqr-border]">
          <h3 className="text-lg font-semibold text-[--clinqr-text-primary]">Choose an image</h3>
          <button type="button" onClick={onClose} className="p-1 text-[--clinqr-text-secondary] hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 border-b border-[--clinqr-border] space-y-3">
          <div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || quotaReached}
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Upload new image
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={handleUpload}
            />
          </div>

          {imageLimit > 0 && (
            <div>
              <div className="flex justify-between items-center mb-1.5">
                <span className="text-xs font-medium text-[--clinqr-text-secondary]">
                  {imageCount} / {imageLimit} images used
                </span>
                {quotaReached && (
                  <a
                    href="/dashboard/billing"
                    className="text-xs font-medium text-[--clinqr-primary] hover:underline"
                  >
                    Upgrade for more images
                  </a>
                )}
              </div>
              <div className="w-full h-1.5 bg-[--clinqr-bg-subtle] rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${quotaReached ? 'bg-red-500' : 'bg-[--clinqr-primary]'}`}
                  style={{ width: `${Math.min(100, (imageCount / imageLimit) * 100)}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-hidden p-4 relative">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-[--clinqr-primary]" />
            </div>
          ) : images.length === 0 ? (
            <p className="text-center text-[--clinqr-text-secondary] py-8">
              No images yet — upload one above to get started
            </p>
          ) : (
            <div className="relative flex items-center">
              <button
                type="button"
                onClick={() => scrollByAmount(-320)}
                className="hidden sm:flex absolute -left-2 z-10 items-center justify-center w-8 h-8 rounded-full bg-white border border-[--clinqr-border] shadow-sm text-[--clinqr-text-secondary] hover:text-[--clinqr-primary] hover:border-[--clinqr-primary] transition-colors"
                aria-label="Scroll left"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <div
                ref={trackRef}
                className="flex gap-3 overflow-x-auto scroll-smooth snap-x snap-mandatory px-1 py-1 sm:px-8"
              >
                {images.map((image) => (
                  <button
                    key={image.id}
                    type="button"
                    onClick={() => onSelect(image)}
                    className="flex-shrink-0 w-28 h-28 snap-start bg-[--clinqr-bg-subtle] rounded-lg overflow-hidden border-2 border-transparent hover:border-[--clinqr-primary] transition-colors"
                    title={image.filename}
                  >
                    <img src={image.url} alt={image.filename} className="w-full h-full object-contain" />
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={() => scrollByAmount(320)}
                className="hidden sm:flex absolute -right-2 z-10 items-center justify-center w-8 h-8 rounded-full bg-white border border-[--clinqr-border] shadow-sm text-[--clinqr-text-secondary] hover:text-[--clinqr-primary] hover:border-[--clinqr-primary] transition-colors"
                aria-label="Scroll right"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
